/** biome-ignore-all lint/correctness/useUniqueElementIds: <TEST> */

import cn from 'classnames';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Input, Loader, ModalComponent, Switch, showNotification } from 'sonata-react-components';

import { postUserImage, putUser } from '@/services/user.service';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';

import './myProfile.styles.scss';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => Promise<void>;
};

type State = {
  view: 'profile' | 'password';
  isLoading: boolean;
  editName: boolean;
};

type UserState = {
  avatar: string;
  email: string;
  name: string;
};

type PassFormState = {
  confirm: string;
  new: string;
  old: string;
};

type TouchedState = {
  confirm: boolean;
  name: boolean;
  new: boolean;
  old: boolean;
};

export function MyProfile({ isOpen = false, onClose, onSave }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { formatMessage } = useIntl();

  const currentUser = useGlobalStore(getUser).entity;
  const { email, image: avatar, name } = currentUser;

  const [{ view, isLoading, editName }, setState] = useState<State>({
    editName: false,
    isLoading: false,
    view: 'profile'
  });

  const [user, setUser] = useState<UserState>({
    avatar: avatar.image
      ? `${taptap.config.staticsUrlBase}${avatar.image}`
      : `${taptap.config.staticsUrlBase}/assets/img/ellipse.svg`,
    email,
    name
  });
  const [passForm, setPassForm] = useState<PassFormState>({
    confirm: '',
    new: '',
    old: ''
  });
  const [touched, setTouched] = useState<TouchedState>({
    confirm: false,
    name: false,
    new: false,
    old: false
  });

  const errors = {
    confirmEqual: touched.confirm && passForm.confirm !== passForm.new,
    confirmLength: touched.confirm && passForm.confirm.length < 6,
    name: touched.name && user.name.length < 6,
    new: touched.new && passForm.new.length < 6,
    old: touched.old && passForm.old.length < 6
  };

  const handlePassChange = (field: keyof TouchedState) => {
    if (!touched[field]) {
      setTouched(prev => ({ ...prev, [field]: true }));
    }
  };

  const imageToken: Record<string, any> = {};
  const userToken: Record<string, any> = {};

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const image = await postUserImage((currentUser._id || currentUser.id)!, file, 0, imageToken);

      await putUser(
        {
          ...currentUser,
          image
        },
        userToken
      );
      await onSave?.();
      setUser(prev => ({
        ...prev,
        avatar: `${taptap.config.staticsUrlBase}${image.image}`
      }));
    }
  };

  const handleEditNameClick = () => {
    if (editName && errors.name) {
      handleNameChange(currentUser.name);
    }
    setState(prev => ({ ...prev, editName: !editName }));
  };

  const handleNameChange = (newName: string) => {
    if (!touched.name) {
      setTouched(prev => ({ ...prev, name: true }));
    }
    setUser({ ...user, name: newName });
  };

  const handleSubmitPassword = async () => {
    const hasErrors =
      passForm.new.length < 6 || passForm.old.length < 6 || passForm.new !== passForm.confirm || user.name.length < 6;

    if (hasErrors) {
      setTouched({ confirm: true, name: true, new: true, old: true });
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Some fields are incomplete or invalid',
          id: 'myProfile.notification.incomplete'
        })
      );
      return;
    }

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      await putUser(
        {
          ...currentUser,
          name: user.name,
          ...(passForm.confirm && {
            newPassword: passForm.new,
            newPasswordConfirm: passForm.confirm,
            password: passForm.old
          })
        },
        userToken
      );
      await onSave?.();
      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'An error occurred while attempting to apply the changes',
          id: 'myProfile.notification.success'
        })
      );
      setPassForm({ confirm: '', new: '', old: '' });
      setTouched({ confirm: false, name: false, new: false, old: false });
      setState(prev => ({ ...prev, view: 'profile' }));
    } catch (e: any) {
      if (e.response.data.error.code) {
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'An error occurred while attempting to apply the changes',
            id: `myProfile.notification.${e.response.data.error.code}`
          })
        );
      } else {
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'An error occurred while attempting to apply the changes',
            id: 'myProfile.notification.error'
          })
        );
      }
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleCancelPassword = () => {
    setPassForm({ confirm: '', new: '', old: '' });
    setTouched({ confirm: false, name: false, new: false, old: false });
    setState(prev => ({ ...prev, view: 'profile' }));
  };

  const handleClose = () => {
    handleNameChange(user.name);
    handleCancelPassword();
    onClose();
  };

  const shouldChangePasswordDisabled =
    isLoading || Object.values(errors).some(e => e) || !passForm.old || !passForm.new;

  return (
    <ModalComponent
      circleCloseButton={view === 'profile'}
      isModalOpened={isOpen}
      onClose={handleClose}
      portalId="ModalComponentID"
      theme="light"
      title={
        view === 'profile'
          ? ''
          : formatMessage({
              defaultMessage: 'Reset your password',
              id: 'myProfile.modal.resetPaswordTitle'
            })
      }
      width="741px"
    >
      <ModalComponent.ModalContent>
        <Switch>
          <Switch.Case condition={view === 'profile'}>
            <main className="myProfile">
              <div className="myProfile__viewContainer myProfile__viewContainer--fadeIn">
                <header className="myProfile__viewHeader">
                  {/** biome-ignore lint/a11y/noNoninteractiveElementInteractions: CUSTOM */}
                  <div className="myProfile__viewHeaderAvatarWrapper" onClick={() => fileInputRef.current?.click()}>
                    <img
                      alt={formatMessage({
                        defaultMessage: 'Profile image',
                        id: 'myProfile.avatar.alt'
                      })}
                      className="myProfile__viewHeaderAvatarImg"
                      src={user.avatar}
                    />
                    <div className="myProfile__viewHeaderAvatarOverlay">
                      <i className="far fa-edit myProfile__viewHeaderAvatarOverlayIcon" />
                      <div className="myProfile__viewHeaderAvatarOverlayText">
                        {formatMessage({
                          defaultMessage: 'Edit picture',
                          id: 'myProfile.avatar.editPicture'
                        })}
                      </div>
                    </div>
                    <input accept="image/*" hidden onChange={handleImageChange} ref={fileInputRef} type="file" />
                  </div>
                  <h2 className="myProfile__viewHeaderTitle">
                    {formatMessage({
                      defaultMessage: 'My profile',
                      id: 'myProfile.avatar.title'
                    })}
                  </h2>
                  <div className="myProfile__viewHeaderSeparator" />
                </header>
                <form className="myProfile__form" onSubmit={e => e.preventDefault()}>
                  <div className="myProfile__formInputWrapper">
                    <Input
                      className={cn('myProfile__formInputName', { 'myProfile__formInputName--disabled': !editName })}
                      id="name-input"
                      inputStatus={editName ? 'warning' : 'default'}
                      label={formatMessage({
                        defaultMessage: 'Name',
                        id: 'myProfile.name.label'
                      })}
                      name="name-input"
                      onChange={value => handleNameChange(value as string)}
                      placeholder={formatMessage({
                        defaultMessage: 'My name',
                        id: 'myProfile.name.placeholder'
                      })}
                      shape="square"
                      type="text"
                      value={user.name}
                    />
                    <span className="myProfile__formInputIcon">
                      <ButtonUI
                        icon="far fa-edit"
                        onClick={handleEditNameClick}
                        shape="link"
                        size="small"
                        type="secondary"
                      />
                    </span>
                  </div>
                  <Input
                    disabled
                    id="email-input"
                    label={formatMessage({
                      defaultMessage: 'Email',
                      id: 'myProfile.email.label'
                    })}
                    name="email-input"
                    shape="square"
                    type="text"
                    value={user.email}
                  />
                </form>
              </div>
              <div className="myPassword__buttons">
                <ButtonUI
                  disabled={false}
                  onClick={() => setState(prev => ({ ...prev, view: 'password' }))}
                  text={formatMessage({
                    defaultMessage: 'Change pasword',
                    id: 'myProfile.button.changePassword'
                  })}
                />
              </div>
            </main>
          </Switch.Case>
          <Switch.Case condition={view === 'password'}>
            <main className="myPassword">
              {isLoading ? (
                <Loader className="myPassword__spinner" id="my-profile-password" type="landing" variant="default">
                  <Loader.Title className="myPassword__spinnerTitle">
                    {formatMessage({
                      defaultMessage: 'Almost there!',
                      id: 'myProfile.spinner.title'
                    })}
                  </Loader.Title>
                  <Loader.Subtitle className="myPassword__spinnerSubtitle">
                    <span>
                      {formatMessage({
                        defaultMessage: 'We’re updating your password.',
                        id: 'myProfile.spinner.subtitle1'
                      })}
                    </span>
                    <span>
                      {formatMessage({
                        defaultMessage: 'This will only take a moment.',
                        id: 'myProfile.spinner.subtitle2'
                      })}
                    </span>
                  </Loader.Subtitle>
                </Loader>
              ) : (
                <form
                  className="myPassword__viewContainer myPassword__viewContainer--fadeIn"
                  onSubmit={handleSubmitPassword}
                >
                  <div>
                    <Input
                      description={
                        errors.old
                          ? formatMessage({
                              defaultMessage: 'Password is too short. Use at least 6 characters.',
                              id: 'myProfile.form.error.length'
                            })
                          : ''
                      }
                      id="old-pass"
                      inputStatus={errors.old ? 'wrong' : 'default'}
                      label={formatMessage({
                        defaultMessage: 'Current password',
                        id: 'myProfile.currentPassword.label'
                      })}
                      name="old-pass"
                      onChange={value => setPassForm({ ...passForm, old: value as string })}
                      onFinishEdit={() => handlePassChange('old')}
                      placeholder={formatMessage({
                        defaultMessage: 'Type your current password',
                        id: 'myProfile.currentPassword.placeholder'
                      })}
                      required
                      shape="square"
                      type="password"
                      value={passForm.old}
                    />
                  </div>
                  <div className="myPassword__viewContainerRow">
                    <div>
                      <Input
                        description={
                          errors.new
                            ? formatMessage({
                                defaultMessage: 'Password is too short. Use at least 6 characters.',
                                id: 'myProfile.form.error.length'
                              })
                            : ''
                        }
                        id="new-pass"
                        inputStatus={errors.new ? 'wrong' : 'default'}
                        label={formatMessage({
                          defaultMessage: 'New password',
                          id: 'myProfile.newPassword.label'
                        })}
                        name="new-pass"
                        onChange={value => setPassForm({ ...passForm, new: value as string })}
                        onFinishEdit={() => handlePassChange('new')}
                        placeholder={formatMessage({
                          defaultMessage: 'Type new password',
                          id: 'myProfile.newPassword.placeholder'
                        })}
                        required
                        shape="square"
                        type="password"
                        value={passForm.new}
                      />
                    </div>
                    <div>
                      <Input
                        description={
                          errors.confirmLength
                            ? formatMessage({
                                defaultMessage: 'Password is too short. Use at least 6 characters.',
                                id: 'myProfile.form.error.length'
                              })
                            : errors.confirmEqual
                              ? formatMessage({
                                  defaultMessage:
                                    'Passwords don’t match. Please enter the same password in both fields.',
                                  id: 'myProfile.form.error.equal'
                                })
                              : ''
                        }
                        id="confirm-pass"
                        inputStatus={errors.confirmLength || errors.confirmEqual ? 'wrong' : 'default'}
                        label={formatMessage({
                          defaultMessage: 'Confirm new password',
                          id: 'myProfile.confirmNewPassword.label'
                        })}
                        name="confirm-pass"
                        onChange={value => setPassForm({ ...passForm, confirm: value as string })}
                        onFinishEdit={() => handlePassChange('confirm')}
                        placeholder={formatMessage({
                          defaultMessage: 'Confirm new password',
                          id: 'myProfile.confirmNewPassword.label'
                        })}
                        required
                        shape="square"
                        type="password"
                        value={passForm.confirm}
                      />
                    </div>
                  </div>
                </form>
              )}
              <div className="myPassword__buttons">
                <ButtonUI
                  onClick={handleCancelPassword}
                  shape="squareRounded"
                  text={formatMessage({
                    defaultMessage: 'Cancel',
                    id: 'myProfile.button.cancel'
                  })}
                  type="secondary"
                />
                <ButtonUI
                  disabled={shouldChangePasswordDisabled}
                  onClick={handleSubmitPassword}
                  shape="squareRounded"
                  text={formatMessage({
                    defaultMessage: 'Change pasword',
                    id: 'myProfile.button.changePassword'
                  })}
                  type="secondary"
                />
              </div>
            </main>
          </Switch.Case>
        </Switch>
      </ModalComponent.ModalContent>
    </ModalComponent>
  );
}
